<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import moment from 'moment';
import { bytes, date } from '@/filtersNew';
import prettyMilliseconds from 'pretty-ms';
import VueSlider from 'vue-3-slider-component';
import IconBase from '@/components/icon/IconBase.vue';
import IconDownload from '@/components/icon/IconDownload.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import Alert from '@/components/Atoms/Alert.vue';
import Badge from '@/components/Badge.vue';
import ModalBox from '@/components/modals/ModalBox.vue';
import { onModalShow, onModalHide } from '@/Utils/modalBus';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import { DEFAULT_AUDIO_TRACKS, audioTrackName, loadDvrAudioTracks } from '@/Utils/dvrAudioTracks';

const props = defineProps({
	video: {
		type: Object,
		required: true,
	},
	action: {
		type: String,
		default: 'video',
	},
	stream: {
		type: Object,
		required: true,
	},
	vodStorageExceeded: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['request-dvr-archive-export-custom-range', 'modal-confirm']);

// Refs
const processing = ref(false);
const isVisible = ref(false);
const active = ref(false);
const rangeStart = ref(0);
const rangeEnd = ref(0);
const slider = ref(0);
const timeline = ref({
	start: 0,
	end: 0,
});
const playerLoading = ref(true);
const playerContainer = ref(null);
const duration = ref(0);
const startInput = ref('');
const endInput = ref('');
let playerInstance = null;
let bitmovinScriptPromise = null;
const vodStorageExceededTooltip = 'Saving as VOD is disabled because your storage limit is exceeded. Upgrade your plan for more storage.';
const audioTrackDropdown = ref(null);

const { proxy } = getCurrentInstance();

// Computed
const rangeStartComp = computed(() => rangeStart.value * 1000);
const rangeEndComp = computed(() => rangeEnd.value * 1000);
const availableAudioTracks = ref(DEFAULT_AUDIO_TRACKS);
const audioTracks = computed(() => {
	const tracks = availableAudioTracks.value.map(audioTrackName);
	if (props.video.audioTrack?.name && tracks.includes(props.video.audioTrack.name)) {
		const filteredTracks = tracks.filter((track) => track !== props.video.audioTrack.name);
		return [props.video.audioTrack.name, ...filteredTracks];
	}
	return tracks;
});
const title = computed(() => {
	if (props.stream.type === 'scheduled') {
		return props.video.fileName;
	}
	switch (props.action) {
		case 'save-as-vod-video':
			return 'Save as VOD';
		case 'download-video':
			return 'Download video';
		case 'download-audio':
			return 'Download audio';
		default:
			return 'Preview video';
	}
});
// Methods
const sliderMin = computed(() => props.video.from);
const sliderMax = computed(() => props.video.from + props.video.duration);
const rangeModel = computed({
	get: () => [rangeStart.value, rangeEnd.value],
	set: (val) => {
		rangeStart.value = val[0];
		rangeEnd.value = val[1];
		duration.value = rangeEnd.value - rangeStart.value;
	},
});
const reloadSource = async () => {
	if (!playerInstance) return;
	playerLoading.value = true;
	try {
		await playerInstance.load({ hls: getPlaybackUrl() });
		playerLoading.value = false;
	} catch (_e) {
		playerLoading.value = false;
	}
};
const onRangeChange = (val) => {
	if (Array.isArray(val)) {
		rangeStart.value = val[0];
		rangeEnd.value = val[1];
		duration.value = val[1] - val[0];
	}
	// reload only the selected section (HLS archive url is range-based)
	reloadSource();
};

const parseTime = (str) => {
	if (typeof str !== 'string') return null;
	const parts = str.trim().split(':').map((p) => Number(p));
	if (!parts.length || parts.some((n) => Number.isNaN(n))) return null;
	let secs = 0;
	if (parts.length === 3) secs = parts[0] * 3600 + parts[1] * 60 + parts[2];
	else if (parts.length === 2) secs = parts[0] * 60 + parts[1];
	else secs = parts[0];
	return Math.round(secs);
};

const syncInputs = () => {
	startInput.value = humanTimeRange(rangeStart.value - props.video.from);
	endInput.value = humanTimeRange(rangeEnd.value - props.video.from);
};

const applyStart = () => {
	const sec = parseTime(startInput.value);
	if (sec === null) return syncInputs();
	let abs = props.video.from + sec;
	abs = Math.max(props.video.from, Math.min(abs, rangeEnd.value - 1));
	rangeStart.value = abs;
	duration.value = rangeEnd.value - rangeStart.value;
	syncInputs();
	reloadSource();
};

const applyEnd = () => {
	const sec = parseTime(endInput.value);
	if (sec === null) return syncInputs();
	let abs = props.video.from + sec;
	abs = Math.min(props.video.from + props.video.duration, Math.max(abs, rangeStart.value + 1));
	rangeEnd.value = abs;
	duration.value = rangeEnd.value - rangeStart.value;
	syncInputs();
	reloadSource();
};

const isFullRange = computed(() => rangeStart.value === props.video.from && rangeEnd.value === props.video.from + props.video.duration);

const resetRange = () => {
	rangeStart.value = props.video.from;
	rangeEnd.value = props.video.from + props.video.duration;
	duration.value = props.video.duration;
	syncInputs();
	reloadSource();
};

const loadBitmovin = () => {
	if (window.bitmovin?.player) return Promise.resolve();
	if (bitmovinScriptPromise) return bitmovinScriptPromise;
	bitmovinScriptPromise = new Promise((resolve, reject) => {
		if (!document.getElementById('bitmovin-ui-css')) {
			const link = document.createElement('link');
			link.id = 'bitmovin-ui-css';
			link.rel = 'stylesheet';
			link.href = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css';
			document.head.appendChild(link);
		}
		const script = document.createElement('script');
		script.src = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer.js';
		script.async = true;
		script.onload = () => resolve();
		script.onerror = reject;
		document.head.appendChild(script);
	});
	return bitmovinScriptPromise;
};

const destroyPlayer = async () => {
	if (playerInstance) {
		try {
			await playerInstance.destroy();
		} catch (_e) {
			// ignore
		}
		playerInstance = null;
	}
};

const loadPlayer = async () => {
	playerLoading.value = true;
	await destroyPlayer();
	try {
		await loadBitmovin();
	} catch (_e) {
		playerLoading.value = false;
		return;
	}
	await nextTick();
	if (!playerContainer.value || !window.bitmovin?.player) {
		playerLoading.value = false;
		return;
	}
	const config = {
		key: 'b495d0ae-5e81-4090-8529-fc00bf780cd9',
		playback: { autoplay: true, muted: true },
		style: { width: '100%' },
	};
	try {
		playerInstance = new window.bitmovin.player.Player(playerContainer.value, config);
		playerInstance.on('playing', () => { playerLoading.value = false; });
		playerInstance.on('error', () => { playerLoading.value = false; });
		await playerInstance.load({ hls: getPlaybackUrl() });
		playerLoading.value = false;
	} catch (_e) {
		playerLoading.value = false;
	}
};

const onExportCustomRange = () => {
	if (rangeEnd.value === 0) {
		rangeEnd.value = props.video.duration;
	}
	const customRange = {
		from: rangeStart.value,
		duration: rangeEnd.value - rangeStart.value,
	};
	if (props.stream.type === 'restream') {
		emit('request-dvr-archive-export-custom-range', customRange);
		dismiss();
		return;
	}
	props.video.customRange = customRange;
	emit('request-dvr-archive-export-custom-range', props.video);
	dismiss();
};

const onDownloadCustomRange = () => {
	try {
		if (rangeEnd.value === 0) {
			rangeEnd.value = props.video.duration;
		}
		const customRange = {
			from: rangeStart.value,
			duration: rangeEnd.value - rangeStart.value,
		};
		if (props.video.audioTrack) {
			switch (props.video.audioTrack.name) {
				case 'Audio track 1':
					props.video.filterTrack = 'v1a1';
					break;
				case 'Audio track 2':
					props.video.filterTrack = 'v1a2';
					break;
				case 'Audio track 3':
					props.video.filterTrack = 'v1a3';
					break;
				case 'Audio track 4':
					props.video.filterTrack = 'v1a4';
					break;
				default:
					break;
			}
			if (props.video.audioTrack.audioOnly) {
				props.video.filterTrack = props.video.filterTrack.replace('v1', '');
			}
		}
		props.video.downloadUrl = props.video.downloadUrl.replace('.castr.io', '.global.ssl.fastly.net');
		const splitDnUrl = props.video.downloadUrl.split('archive-');
		let downloadUrl = `${splitDnUrl[0]}archive-${customRange.from}-${customRange.duration}.mp4?packing=compat`;
		if (props.video.filterTrack) {
			downloadUrl += `&filter.tracks=${props.video.filterTrack}`;
		}
		window.location.href = downloadUrl;
		dismiss();
	} catch (e) {
		console.log('Error downloading selected region of live-to-vod', e);
	}
};

const onInit = () => {
	active.value = true;
};

const onDismiss = () => {
	active.value = false;
	rangeStart.value = 0;
	rangeEnd.value = 0;
};

const dismiss = () => {
	isVisible.value = false;
	setTimeout(() => {
		processing.value = false;
	}, 1000);
};

const onRootModalShow = (modalId) => {
	if (modalId !== 'modal-dvr-archive-video-preview') {
		return;
	}
	isVisible.value = true;
};

const onRootModalHide = (modalId) => {
	if (modalId !== 'modal-dvr-archive-video-preview') {
		return;
	}
	isVisible.value = false;
};

const initSlider = () => {
	rangeStart.value = props.video.from;
	rangeEnd.value = props.video.from + props.video.duration;
	syncInputs();
};

const getPlaybackUrl = () => {
	timeline.value.start = props.video.from * 1000;
	timeline.value.end = (props.video.from + props.video.duration) * 1000;

	if (!rangeEnd.value) {
		rangeStart.value = props.video.from;
		rangeEnd.value = props.video.from + props.video.duration;
	}
	const from = rangeStart.value || props.video.from;
	const selectedDuration = (rangeEnd.value - rangeStart.value) || props.video.duration;
	const srcServer = props.video.server;
	const range = `${from}-${selectedDuration}`;
	const prefix = props.video.abr ? 'edge-en' : 'edge';
	const key = props.video.abr ? `ngrp:${props.stream.key}_all` : props.stream.key;

	let url = `https://${srcServer}/archives/${prefix}/${key}/archive-${range}.m3u8`;
	if (props.stream.type === 'restream') {
		url = `https://${srcServer}/store/${key}/archive-${range}.m3u8`;
	}
	if (props.stream.type === 'scheduled') {
		if (props.video.type === 'live-to-vod') {
			url = `https://vod-archives.castr.com/${props.video.id}/index.m3u8`;
		} else if (props.video.type === 'imported') {
			url = `https://cstr-vod.castr.com/${props.video.importedVideoId}/index.m3u8`;
		} else {
			url = `https://castr-preview.global.ssl.fastly.net/recorded-preview/${props.stream._id}/${props.video.id}/index.m3u8`;
		}
		slider.value = '1';
	}
	if (props.video.namedHls) {
		url = `https://${srcServer}/${props.video.episodeId}/archive-${range}.m3u8`;
	}
	if (props.video.audioTrack) {
		switch (props.video.audioTrack.name) {
			case 'Audio track 1':
				url += '?filter.tracks=v1a1';
				break;
			case 'Audio track 2':
				url += '?filter.tracks=v1a2';
				break;
			case 'Audio track 3':
				url += '?filter.tracks=v1a3';
				break;
			case 'Audio track 4':
				url += '?filter.tracks=v1a4';
				break;
			default:
				break;
		}
		if (props.video.audioTrack.audioOnly) {
			url = url.replace('v1', '');
		}
	}
	return url;
};

// Format a duration as H:mm:ss. Note: moment.utc(...).format('HH:mm:ss') wraps
// hours at 24 (time-of-day), which truncated the label for recordings longer
// than a day (e.g. 69:40:06 showed as 21:40:06); accumulate hours instead.
const humanTimeRange = (timeInSeconds) => {
	const total = Math.max(0, Math.floor(Number(timeInSeconds) || 0));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const pad = (n) => String(n).padStart(2, '0');
	return `${pad(h)}:${pad(m)}:${pad(s)}`;
};
const humanTime = (timeInSeconds) => prettyMilliseconds(timeInSeconds * 1000, { unitCount: 2 });
const bytesFormated = (item) => bytes(item);
const dateFormated = (item) => date(item);

const selectAudioTrack = (trackName) => {
	if (props.video.audioTrack) {
		props.video.audioTrack.name = trackName;
	} else {
		props.video.audioTrack = { name: trackName };
	}
	audioTrackDropdown.value?.toggleDropdown();
};

let unsubModalShow;
let unsubModalHide;

onMounted(() => {
	unsubModalShow = onModalShow(onRootModalShow);
	unsubModalHide = onModalHide(onRootModalHide);
});

onBeforeUnmount(() => {
	unsubModalShow?.();
	unsubModalHide?.();
	destroyPlayer();
});

watch(isVisible, (value) => {
	if (value) {
		initSlider();
		onInit();
		nextTick(() => loadPlayer());
		if (props.stream.type !== 'scheduled') {
			loadDvrAudioTracks(props.video, props.stream).then((tracks) => {
				availableAudioTracks.value = tracks;
			});
		}
		return;
	}
	onDismiss();
	destroyPlayer();
});

watch(
	() => props.video?.audioTrack?.name,
	() => {
		if (isVisible.value) loadPlayer();
	}
);

watch([rangeStart, rangeEnd], () => syncInputs());
</script>

<template>
	<ModalBox v-model="isVisible" :size="stream.type === 'scheduled' ? 'md' : 'lg'" headerClass="!p-2 -mb-8"
		:wrapper-classes="stream.type === 'scheduled' ? 'lg:!h-auto lg:!max-h-none' : ''">
		<!-- Scheduled: compact, player-first preview — the user just wants to watch the video -->
		<div v-if="stream.type === 'scheduled'" class="!px-4 !pb-4 -mt-2">
			<div class="mb-3 min-w-0">
				<p class="text-tiny font-medium uppercase tracking-wider text-surface-6 mb-0.5">Preview</p>
				<h3 class="text-base font-medium text-white break-words line-clamp-2 mb-0" :title="title">{{ title }}</h3>
			</div>
			<div class="relative aspect-video w-full rounded-lg overflow-hidden bg-black">
				<div v-if="playerLoading"
					class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-surface-8 pointer-events-none">
					<span class="inline-block size-7 rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
					<span class="text-sm">Loading preview…</span>
				</div>
				<div ref="playerContainer" class="absolute inset-0 w-full h-full"></div>
			</div>
			<!-- Metadata -->
			<div class="flex flex-wrap items-center gap-x-6 gap-y-1.5 mt-3">
				<div class="flex items-center gap-1.5">
					<span class="text-tiny text-surface-7">Duration</span>
					<span class="text-tiny text-surface-text-1 font-medium tabular-nums">{{ humanTimeRange(video?.mediaInfo?.duration) }}</span>
				</div>
				<div v-if="video.mediaInfo && video.mediaInfo.width && video.mediaInfo.height" class="flex items-center gap-1.5">
					<span class="text-tiny text-surface-7">Resolution</span>
					<span class="text-tiny text-surface-text-1 font-medium tabular-nums">{{ video.mediaInfo.width }} × {{ video.mediaInfo.height }}</span>
				</div>
				<div v-if="video.size" class="flex items-center gap-1.5">
					<span class="text-tiny text-surface-7">Size</span>
					<span class="text-tiny text-surface-text-1 font-medium">{{ bytesFormated(video.size) }}</span>
				</div>
			</div>
		</div>
		<!-- DVR / live: full clip-selection + download layout -->
		<div v-else class="grid grid-cols-12 gap-4 !py-2 !px-4">
			<div class="col-span-12 md:col-span-7 relative">
				<!-- iframe widget -->
				<div class="relative aspect-video w-full rounded-lg overflow-hidden bg-black">
					<div v-if="playerLoading"
						class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-surface-8 pointer-events-none">
						<span class="inline-block size-7 rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
						<span class="text-sm">Loading preview…</span>
					</div>
					<div ref="playerContainer" class="absolute inset-0 w-full h-full"></div>
				</div>
				<div class="mt-3" v-if="stream.type !== 'scheduled'">
					<vue-slider v-model="rangeModel" :min="sliderMin" :max="sliderMax" :interval="1" :enable-cross="false"
						tooltip="none" :height="6" :dot-size="16"
						:rail-style="{ backgroundColor: '#4F5C87', borderRadius: '4px' }"
						:process-style="{ backgroundColor: '#0062FF' }"
						:dot-style="{ backgroundColor: '#ffffff', border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,.4)' }"
						@change="onRangeChange" />
					<div class="flex justify-between text-tiny text-surface-7 mt-2">
						<span>00:00:00</span>
						<span>{{ humanTimeRange(video.duration) }}</span>
					</div>
				</div>
			</div>
			<div class="col-span-12 md:col-span-5 flex flex-col justify-between self-center" v-if="active">
				<h3 class="text-lg font-medium !mb-1 text-white break-words line-clamp-2" :title="title">{{ title }}</h3>
				<p v-if="stream.type != 'scheduled'" class="text-tiny text-surface-7 !mb-4">Drag the handles or enter exact
					start and end times to select a range.</p>
				<drop-down-menu v-if="stream.type !== 'scheduled'" class="" type="flat" fullWidth ref="audioTrackDropdown">
					<template #toggle-button>
						{{ video.audioTrack?.name }}
						<icon-base>
							<icon-chevron-down />
						</icon-base>
					</template>
					<template #menu-items>
						<drop-down-menu-item v-for="(item, index) in audioTracks" :key="index" @click="selectAudioTrack(item)">
							{{ item }}
						</drop-down-menu-item>
					</template>
				</drop-down-menu>
				<div v-if="stream.type !== 'scheduled'" class="grid grid-cols-2 gap-3 mt-4 mb-2">
					<div>
						<label class="block text-tiny text-surface-7 mb-1.5">Start time</label>
						<FormInput v-model="startInput" placeholder="00:00:00" @blur="applyStart" @keyup.enter="applyStart" />
					</div>
					<div>
						<label class="block text-tiny text-surface-7 mb-1.5">End time</label>
						<FormInput v-model="endInput" placeholder="00:00:00" @blur="applyEnd" @keyup.enter="applyEnd" />
					</div>
				</div>
				<div v-if="stream.type !== 'scheduled'" class="flex items-center justify-between text-tiny mb-4">
					<span class="text-surface-7">Clip length:
						<span class="text-white ml-1">{{ humanTimeRange(rangeEnd - rangeStart) }}</span>
					</span>
					<FormButton v-if="!isFullRange" @click="resetRange"
						custom-classes="text-main-light hover:text-white transition-colors">Reset</FormButton>
				</div>
				<FormButton v-if="stream.type !== 'scheduled'" @click="onDownloadCustomRange" fullWidth class="justify-center gap-2 !mb-2" type="secondary">
					<icon-base>
						<icon-download />
					</icon-base>
					Audio track only
				</FormButton>
				<FormButton v-if="stream.type !== 'scheduled'" @click="onDownloadCustomRange" fullWidth class="justify-center gap-2 !mb-2" type="secondary">
					<icon-base>
						<icon-download />
					</icon-base>
					Download video
				</FormButton>
				<Tooltip v-if="stream.type !== 'scheduled'" :active="stream.type === 'live' && vodStorageExceeded" :content="vodStorageExceededTooltip"
					class="w-full inline-block" tabindex="0">
					<FormButton v-if="stream.type !== 'scheduled'" @click="onExportCustomRange" class="justify-center gap-2" fullWidth
						:disabled="stream.type === 'live' && vodStorageExceeded" full-width>
						{{ stream.type === 'live' ? 'Save as VOD' : 'Download Range' }}
					</FormButton>
				</Tooltip>
			</div>
		</div>
	</ModalBox>
</template>